import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeamStore } from '../store/teamStore';
import { toast } from 'react-hot-toast';
import { Plus, Calendar, BarChart3, ArrowRight, Home, Plane } from 'lucide-react';
import { gameService, type Game } from '../services/gameService';
import { CreateGameWizard } from './games/CreateGameWizard';
import PageHeader from './layout/PageHeader';
import type { Sport } from '../types/core';

export const GamesManagement: React.FC = () => {
  const { currentTeam } = useTeamStore();
  const navigate = useNavigate();
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateWizard, setShowCreateWizard] = useState(false);

  useEffect(() => {
    if (currentTeam?.id) {
      loadGames();
    }
  }, [currentTeam?.id]);

  const loadGames = async () => {
    if (!currentTeam?.id) return;

    setLoading(true);
    try {
      const gamesData = await gameService.getGames(currentTeam.id);
      setGames(gamesData);
    } catch (error) {
      console.error('Error loading games:', error);
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const handleGameCreated = (game: Game) => {
    loadGames();
    // Navigate to game detail view
    navigate(`/games/${game.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Only show for baseball/softball
  if (currentTeam?.sport !== 'Baseball' && currentTeam?.sport !== 'Softball') {
    return (
      <div className="text-center py-12">
        <BarChart3 className="mx-auto text-gray-400 mb-4" size={48} />
        <p className="text-gray-600">Game statistics are only available for Baseball and Softball teams.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Games"
        icon={<Calendar className="h-6 w-6" />}
        actions={
          <button
            onClick={() => setShowCreateWizard(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={16} />
            Create Game
          </button>
        }
      />

      {games.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600 mb-2">No games created yet</p>
          <p className="text-sm text-gray-500 mb-4">Click "Create Game" to add a new game</p>
          <button
            onClick={() => setShowCreateWizard(true)}
            className="btn-primary"
          >
            Create Game
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {games.map((game) => (
            <div
              key={game.id}
              onClick={() => navigate(`/games/${game.id}`)}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {game.opponent_name}
                  </h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(game.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    {game.game_time && (
                      <div className="flex items-center gap-2">
                        <span>{new Date(`2000-01-01T${game.game_time}`).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}</span>
                      </div>
                    )}
                    {game.location && (
                      <div className="flex items-center gap-2">
                        <span>{game.location}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {game.home_away === 'home' ? (
                    <Home className="w-5 h-5 text-primary" />
                  ) : (
                    <Plane className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-xs text-gray-500">
                  {game.home_away === 'home' ? 'Home' : 'Away'}
                </span>
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Game Wizard */}
      {currentTeam && showCreateWizard && (
        <CreateGameWizard
          isOpen={showCreateWizard}
          onClose={() => setShowCreateWizard(false)}
          teamId={currentTeam.id}
          onGameCreated={handleGameCreated}
        />
      )}
    </div>
  );
};

